import { Alert, Form, message, Modal, ModalProps, Select } from '@pankod/refine-antd'
import { useTranslate } from '@pankod/refine-core'
import CustomSkeleton from 'components/feedback/CustomSkeleton'
import { Supplement } from 'interfaces'
import { useCallback, useEffect, useRef, useState } from 'react'
import { checkUserPermission, getIslands, getVariable, postalToIsland, supabaseClient, viewTicket } from 'utility'
import { API_URL, EL_HIERRO_ISLAND_ID, ENDPOINT_CTT, ENDPOINT_DHL, ENDPOINT_TIPSA, FUERTEVENTURA_ISLAND_ID, GRAN_CANARIA_ISLAND_ID, LA_GOMERA_ISLAND_ID, LA_GRACIOSA_ISLAND_ID, LA_PALMA_ISLAND_ID, LANZAROTE_ISLAND_ID, SHIPMENT_TYPE_CONSOLIDATED, SHIPMENT_TYPE_DIRECT, TENERIFE_ISLAND_ID } from 'utility/constants'
import { getWarehousePreparationBlock, warehousePreparationAdviceKey } from 'utility/warehousePreparationGate'
import { getWarehouseReviewDecision, localWarehouseReviewDecision } from 'utility/warehouseReview'
import { hasUncheckedWarehouseReviewItems } from 'utility/warehouseReviewChecklist'

const TF_ISLANDS = [TENERIFE_ISLAND_ID, LA_PALMA_ISLAND_ID, LA_GOMERA_ISLAND_ID, EL_HIERRO_ISLAND_ID]
const GC_ISLANDS = [GRAN_CANARIA_ISLAND_ID, LANZAROTE_ISLAND_ID, FUERTEVENTURA_ISLAND_ID, LA_GRACIOSA_ISLAND_ID]

type ProcessModalProps = {
  modalProps: ModalProps,
  close: any,
  orderId: number | undefined,
  setOrderId?: Function | undefined,
  currentOrderInfo: any | null
  warehouseMode?: boolean
  directPreparation?: boolean
  preparationSupplement?: Supplement | null
  onPrepared?: (supplementId?: number | null) => void
  setCurrentOrderInfo?: Function | undefined
  tableQuery?: any
}

export const ProcessModal: React.FC<ProcessModalProps> = ({ modalProps, close, orderId, setOrderId, currentOrderInfo, setCurrentOrderInfo, tableQuery, warehouseMode = false, directPreparation = false, preparationSupplement = null, onPrepared }) => {
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [shipperEndpoint, setShipperEndpoint] = useState('')
  const [shipperLabel, setShipperLabel] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [loading, setLoading] = useState(true)
  const [shippedBy, setShippedBy] = useState<string | undefined>(undefined)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const directPreparationStarted = useRef(false)
  const t = useTranslate()
  const [resolvedRulesKey, setResolvedRulesKey] = useState<string | null>(null)
  const orderData = currentOrderInfo?.order || currentOrderInfo
  const shippingAddress = orderData?.shipping_address_id?.island_id ? orderData.shipping_address_id : orderData?.shipping_address
  // Admission can arrive before the order query refreshes. Use the saved
  // supplement, including its weights, rather than an older query result.
  const volumetricSupplement = preparationSupplement && !preparationSupplement.deleted && preparationSupplement.volumetric
    ? preparationSupplement
    : orderData?.supplements?.find((s: Supplement) => !s.deleted && s.volumetric)
  const rulesKey = JSON.stringify({
    visible: !!modalProps.visible,
    orderId,
    id: orderData?.id,
    postalCode: shippingAddress?.postal_code,
    method: orderData?.shipping_method?.name,
    volumetricSupplement: volumetricSupplement && {
      id: volumetricSupplement.id,
      billable_volume: volumetricSupplement.billable_volume,
      volumetricInfo: volumetricSupplement.volumetricInfo
    }
  })

  const userEmail = supabaseClient.auth.user()?.email

  useEffect(() => {
    const fetchPermission = async () => {
      setHasPermission(await checkUserPermission())
    }
    fetchPermission()
  }, [])

  useEffect(() => {
    const { orderId, id, postalCode, method, volumetricSupplement } = JSON.parse(rulesKey)
    let cancelled = false
    setResolvedRulesKey(null)
    setLoading(true)
    setErrorMsg('')
    setShipperEndpoint('')
    setShippedBy(undefined)
    const applyRules = async () => {
      const isExpress = method?.toLowerCase().includes('express')
      const [islands, lengthSum, length, weight] = await Promise.all([
        getIslands(),
        ...(isExpress ? [] : [getVariable('MAX_LENGTH_SUM_CTT'), getVariable('MAX_LENGTH_CTT'), getVariable('MAX_WEIGHT')])
      ])
      if (cancelled) return
      const island = postalToIsland(islands, postalCode)
      const islandId = island?.id || 1

      if (isExpress) {
        setShipperEndpoint(`${ENDPOINT_TIPSA}/${orderId}/${SHIPMENT_TYPE_DIRECT}`)
        setShipperLabel(t('shipments.methods.tipsaMainland'))
        setShippedBy('TIPSA PENÍNSULA')
      } else {
        const maxLengthSumCTT = Number(lengthSum)
        const maxLengthCTT = Number(length)
        const maxWeight = Number(weight)
        const volSup = volumetricSupplement

        let canCTT = true
        let totalWeigth = 0 // total weight is billVolume
        if (volSup && volSup.volumetricInfo) {
          volSup.volumetricInfo.forEach((packageValue: { weigth: number, depth: number, heigth: number, length: number, billVolume: number }) => {
            if (packageValue) {
              totalWeigth += packageValue.billVolume
              if (packageValue.depth > maxLengthCTT || packageValue.heigth > maxLengthCTT || packageValue.length > maxLengthCTT) canCTT = false
              const total = packageValue.depth + packageValue.heigth + packageValue.length
              if (total > maxLengthSumCTT) canCTT = false
            }
          })
        }

        // DHL
        if (totalWeigth > maxWeight || volSup?.billable_volume > maxWeight) {
          if (GC_ISLANDS.includes(islandId)) {
            setShipperEndpoint(`${ENDPOINT_DHL}/${orderId}?agency=gc`)
            setShippedBy('DHL GC')
            setShipperLabel(t('shipments.methods.dhlGC'))
          } else if (TF_ISLANDS.includes(islandId)) {
            setShipperEndpoint(`${ENDPOINT_DHL}/${orderId}?agency=tf`)
            setShippedBy('DHL TF')
            setShipperLabel(t('shipments.methods.dhlTF'))
          }
        } else {
          // If ship to GC islands -> ctt / in other case Tipsa
          if (GC_ISLANDS.includes(islandId)) {
            // if ctt restrictions are not passed
            if (!canCTT) {
              setShipperEndpoint(`${ENDPOINT_DHL}/${orderId}?agency=gc`)
              setShippedBy('DHL GC')
              setShipperLabel(t('shipments.methods.dhlGC'))
            } else {
              setShipperEndpoint(`${ENDPOINT_CTT}/${orderId}`)
              setShippedBy('CTT GC')
              setShipperLabel(t('shipments.methods.cttGC'))
            }
          } else {
            setShipperEndpoint(`${ENDPOINT_TIPSA}/${orderId}/${SHIPMENT_TYPE_CONSOLIDATED}?agency=tf`)
            setShippedBy('TIPSA TF')
            setShipperLabel(t('shipments.methods.tipsaTF'))
          }
        }
      }

      if (!cancelled) {
        setResolvedRulesKey(rulesKey)
        setLoading(false)
      }
    }
    if (orderId && id === orderId) {
      applyRules().catch(() => {
        if (cancelled) return
        setErrorMsg('No se ha podido calcular el transportista. Cierra y vuelve a preparar el envío.')
        setLoading(false)
      })
    }
    return () => { cancelled = true }
  }, [rulesKey, t])

  useEffect(() => {
    if (!modalProps.visible) directPreparationStarted.current = false
  }, [modalProps.visible, orderId])

  const getErrorDescription = useCallback((msg: string): string => {
    if (msg === 'Uncomplete data') return t('shipments.errors.uncompletedData')
    return msg
  }, [t])

  const getWarehouseReviewChecklistBlock = useCallback(async (): Promise<string | null> => {
    if (!warehouseMode || !orderId) return null

    try {
      // The local lane is only an immediate skip for a confirmed no-review
      // outcome.  AliExpress is resolved locally as REVIEW_REQUIRED so that
      // one-reference/one-unit orders cannot bypass the item checklist.
      const localDecision = localWarehouseReviewDecision(currentOrderInfo)
      if (localDecision?.decision_code.startsWith('NO_REVISAR')) return null
      const [decisionResult, itemsResult] = await Promise.allSettled([
        getWarehouseReviewDecision(orderId),
        supabaseClient.from('items')
          .select('disabled, deleted, delivered_warehouse')
          .eq('order_id', orderId)
          .eq('deleted', false)
      ])

      if (itemsResult.status !== 'fulfilled' || itemsResult.value.error || !itemsResult.value.data) return null
      // Missing review data falls back to a manual review, without creating a
      // technical block of its own.
      const decision = decisionResult.status === 'fulfilled' ? decisionResult.value : null
      if (hasUncheckedWarehouseReviewItems(decision, itemsResult.value.data)) return t('supplement.warehouseBlock.reviewChecklist')
      return null
    } catch {
      return null
    }
  }, [currentOrderInfo, orderId, t, warehouseMode])

  const handleOk = useCallback(async (directPreparation = false) => {
    if (resolvedRulesKey !== rulesKey || loading || !shipperEndpoint || !shippedBy) return
    const preparationBlock = warehouseMode ? getWarehousePreparationBlock(currentOrderInfo, preparationSupplement) : null
    if (preparationBlock) {
      const adviceKey = warehousePreparationAdviceKey(currentOrderInfo, 'prepare')
      const description = adviceKey === 'supplement.advise.lastYear'
        ? t('supplement.advise.lastYear', { operation: t('supplement.advise.lastYearPrepare') })
        : t(adviceKey)
      if (directPreparation) {
        close()
        message.error(description)
      } else {
        setErrorMsg(description)
      }
      return
    }
    const reviewChecklistBlock = await getWarehouseReviewChecklistBlock()
    if (reviewChecklistBlock) {
      if (directPreparation) {
        close()
        message.error(reviewChecklistBlock)
      } else {
        setErrorMsg(reviewChecklistBlock)
      }
      return
    }
    setConfirmLoading(true)
    try {
      const response = await fetch(`${API_URL}/${shipperEndpoint}`, {
        method: 'GET',
        headers: {
          'Content-type': 'application/json'
        },
        // TODO: Set same cookies as web when login into the dashboard
        credentials: 'include'
      })

      if (!response.ok) {
        const responseMessage = await response.text()
        const description = getErrorDescription(responseMessage)
        if (directPreparation) {
          close()
          message.error(description)
        } else {
          setErrorMsg(description)
        }
      } else {
        const data = await response.json()

        await supabaseClient.from('shipments').update({ shipped_by: shippedBy }).eq('order_id', orderId)

        if (data.error) {
          const description = getErrorDescription(data.message)
          if (directPreparation) {
            close()
            message.error(description)
          } else {
            setErrorMsg(description)
          }
        } else {
          setConfirmLoading(false)
          onPrepared?.(preparationSupplement?.id)
          close()
          setErrorMsg('')
          // The API only answers OK after storing the label. Open it first so
          // Warehouse can print immediately; UI refresh is not part of that
          // critical path.
          await viewTicket(undefined, orderId)
          if (setOrderId) setOrderId(0)
          if (setCurrentOrderInfo) setCurrentOrderInfo(null)
          setLoading(true)
          setShipperEndpoint('')
          if (tableQuery?.refetch) {
            tableQuery.refetch().catch(() => undefined)
          }
        }
      }
    } catch (e) {
      if (directPreparation) {
        close()
        message.error('something wrong')
      } else {
        setErrorMsg('something wrong')
      }
      if (setOrderId) setOrderId(0)
      if (setCurrentOrderInfo) setCurrentOrderInfo(null)
      setLoading(true)
      setShipperEndpoint('')
    } finally {
      setConfirmLoading(false)
      setShippedBy(undefined)
    }
  }, [resolvedRulesKey, rulesKey, loading, close, currentOrderInfo, getErrorDescription, getWarehouseReviewChecklistBlock, onPrepared, orderId, preparationSupplement, setCurrentOrderInfo, setOrderId, shipperEndpoint, shippedBy, tableQuery, warehouseMode])

  useEffect(() => {
    if (resolvedRulesKey !== rulesKey || !modalProps.visible || !directPreparation || loading || !shipperEndpoint || !shippedBy || directPreparationStarted.current) return

    directPreparationStarted.current = true
    handleOk(true)
  }, [resolvedRulesKey, rulesKey, directPreparation, handleOk, loading, modalProps.visible, shippedBy, shipperEndpoint])

  return (
    <Modal
      {...modalProps}
      // Warehouse launches the automatic flow without administrator rights.
      // The request must remain visible while it is creating the label; the
      // administrator permission still only controls the manual selector.
      visible={modalProps.visible && (hasPermission === true || directPreparation)}
      title={directPreparation ? 'Generando etiqueta…' : t('shipments.actions.processShipment')}
      centered
      closable={!directPreparation || !!errorMsg}
      footer={directPreparation ? null : undefined}
      onOk={() => { handleOk() }}
      onCancel={() => {
        setErrorMsg('')
        if (setOrderId) setOrderId(0)
        if (setCurrentOrderInfo) setCurrentOrderInfo(null)
        setShipperEndpoint('')
        setLoading(true)
        close()
        if (tableQuery) tableQuery.refetch()
      }}
      okButtonProps={{ disabled: loading || resolvedRulesKey !== rulesKey || !shipperEndpoint }}
      confirmLoading={confirmLoading}
    >
      {/* <div style={{ display: 'flex', alignItems: 'center', gap: '200px', marginBottom: 10 }}>
        <p>{t('caption.CP')} {orderData?.shipping_address_id?.postal_code}</p>
        <Card size='small' style={{ fontSize: 11 }}>
          <p>{t('caption.TipsaGC')}</p>
          <p>{t('caption.ONTIME')}</p>
          <p>{t('caption.TipsaTF')}</p>
        </Card>
      </div> */}

      {directPreparation && <Alert type="info" showIcon message="Generando etiqueta…" description="La expedición se está creando. Espera un momento." />}
      <CustomSkeleton loading={!loading} children={
        <>
          <b>{t('shipments.actions.autoMessageInfo', { shipper: shipperLabel })}</b>
        </>
      } />
      {(!directPreparation && userEmail && hasPermission) &&
      <Form style={{ marginTop: '2rem' }} layout='vertical'>
        <Form.Item
          label={t('shipments.actions.chooseShipper')}
        >
          <Select
          value={shipperEndpoint}
            onChange={(value) => {
              setShipperEndpoint(value)
              if (value.includes('CTT')) {
                setShippedBy('ctt')
                setShipperLabel(t('shipments.methods.cttGC'))
              } else if (value.includes('DHL GC') && value.includes('agency=gc')) {
                setShippedBy('DHL GC')
                setShipperLabel(t('shipments.methods.dhlGC'))
              } else if (value.includes('DHL TF') && value.includes('agency=tf')) {
                setShippedBy('DHL TF')
                setShipperLabel(t('shipments.methods.dhlTF'))
              } else if (value.includes('agency=tf') && value.includes('TIPSA')) {
                setShippedBy('TIPSA TF')
                setShipperLabel(t('shipments.methods.tipsaTF'))
              } else if (value.toLowerCase().includes('directo')) {
                setShippedBy('TIPSA PENÍNSULA')
                setShipperLabel(t('shipments.methods.tipsaMainland'))
              }
            }}
            options={[
              { value: '', label: '-----------------' },
              { value: `${ENDPOINT_TIPSA}/${orderId}/${SHIPMENT_TYPE_CONSOLIDATED}?agency=gc`, label: t('shipments.methods.tipsaGC') },
              { value: `${ENDPOINT_TIPSA}/${orderId}/${SHIPMENT_TYPE_CONSOLIDATED}?agency=tf`, label: t('shipments.methods.tipsaTF') },
              { value: `${ENDPOINT_TIPSA}/${orderId}/${SHIPMENT_TYPE_DIRECT}`, label: t('shipments.methods.tipsaExpress') },
              // { value: `${ENDPOINT_ONTIME}/${orderId}`, label: t('shipments.methods.ontime') },
              { value: `${ENDPOINT_CTT}/${orderId}`, label: t('shipments.methods.cttGC') },
              { value: `${ENDPOINT_DHL}/${orderId}?agency=gc`, label: t('shipments.methods.dhlGC') },
              { value: `${ENDPOINT_DHL}/${orderId}?agency=tf`, label: t('shipments.methods.dhlTF') }
              // { value: `${ENDPOINT_CORREOS}/${orderId}/${CORREOS_SERVICE_TYPE_STANDARD_STR}`, label: t('shipments.methods.correosStandard') },
              // { value: `${ENDPOINT_CORREOS}/${orderId}/${CORREOS_SERVICE_TYPE_PREMIUM_STR}`, label: t('shipments.methods.correosExpress') }
            ]}
          />
        </Form.Item>
      </Form>}
      {errorMsg && (
        <Alert
          message={'❌ Error:'}
          description={errorMsg}
          type="error"
        />
      )}
    </Modal>
  )
}
